import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { applyDarkModeClass, getStartingDarkModeValue, DarkModeContext, setStoredDarkMode } from "./dark-mode";

const DARK_MODE_KEYBOARD_SHORTCUT = "KeyD";

const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
};

export const DarkModeProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(getStartingDarkModeValue());

  useEffect(() => {
    applyDarkModeClass(darkMode);
    setStoredDarkMode(darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableTarget(event.target)) {
        return;
      }

      if (
        event.code === DARK_MODE_KEYBOARD_SHORTCUT &&
        event.altKey &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        setDarkMode((currentDarkMode) => !currentDarkMode);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const memoValue = useMemo(() => ({ darkMode, setDarkMode }), [darkMode]);

  return <DarkModeContext.Provider value={memoValue}>{children}</DarkModeContext.Provider>;
};
